"use client";

import type React from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/tailwind/ui/avatar";
import { Button } from "@/components/tailwind/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/tailwind/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/tailwind/ui/dropdown-menu";
import { Input } from "@/components/tailwind/ui/input";
import { useFolderContext } from "@/context/FolderContext";
import { type User, getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { addDoc, collection, deleteDoc, doc, getDoc, getFirestore, updateDoc } from "firebase/firestore";
import { ChevronLeft, ChevronRight, Folder, FolderPlus } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface UserFolder {
  uid: string;
  name: string;
  documents: string[];
}

interface NavItemProps {
  href: string;
  label: React.ReactNode;
  icon: React.ReactNode;
  active?: boolean;
  collapsed?: boolean;
}

function NavItem({ href, label, icon, active, collapsed }: NavItemProps) {
  if (collapsed) return null;

  return (
    <Link
      href={href}
      className={`flex items-center px-3 py-2 text-sm rounded-md w-full
        ${active ? "bg-gray-200 font-medium" : "text-gray-700 hover:bg-gray-200"}
      `}
    >
      <span className="flex-shrink-0">{icon}</span>
      <span className="ml-3 flex-1">{label}</span>
    </Link>
  );
}

interface CollapsibleSidebarProps {
  activeItem?: string;
}

export function CollapsibleSidebar({ activeItem }: CollapsibleSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const { folders, setFolders, activeFolderId, setActiveFolderId } = useFolderContext();
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    folderId: string | null;
    folderName: string;
    open: boolean;
  }>({ x: 0, y: 0, folderId: null, folderName: "", open: false });
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [username, setUsername] = useState<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const db = getFirestore();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        fetchFolders(firebaseUser.uid, firebaseUser);
        // Fetch username from userCollections
        try {
          const userDocRef = doc(db, "userCollections", firebaseUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUsername(userData.username || null);
          } else {
            setUsername(null);
          }
        } catch (err) {
          setUsername(null);
        }
      } else {
        setUsername(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchFolders = async (userId: string, userObj: User | null) => {
    if (!userObj) {
      console.log("No user found, skipping folder fetch");
      return;
    }

    try {
      const userDocRef = doc(db, "userCollections", userId);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        console.log("User document does not exist for ID:", userId);
        setFolders([]);
        return;
      }

      const userData = userDocSnap.data();
      console.log("User data:", userData);

      const { folders: folderIds = [] } = userData;

      if (!Array.isArray(folderIds)) {
        console.log("Folder IDs is not an array:", folderIds);
        setFolders([]);
        return;
      }

      if (folderIds.length === 0) {
        console.log("No folder IDs found for user");
        setFolders([]);
        return;
      }

      const foldersPromises = folderIds.map(async (folderId) => {
        const folderDocRef = doc(db, "folders", folderId);
        const folderDocSnap = await getDoc(folderDocRef);

        if (!folderDocSnap.exists()) {
          return null;
        }

        const folderData = folderDocSnap.data();

        return {
          uid: folderId,
          name: folderData.name || "Untitled Folder",
          documents: folderData.documents || [],
        } as UserFolder;
      });

      const foldersData = (await Promise.all(foldersPromises)).filter(Boolean) as UserFolder[];
      console.log("Final folders data:", foldersData);
      setFolders(foldersData);
      if (foldersData.length > 0 && !activeFolderId) {
        setActiveFolderId(foldersData[0].uid);
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
      toast.error("Failed to load folders. Please try refreshing the page.");
    }
  };

  const handleCreateFolder = async (folderName: string) => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    if (!folderName?.trim()) {
      toast.error("Folder name cannot be empty");
      return;
    }

    try {
      const newFolderRef = await addDoc(collection(db, "folders"), {
        name: folderName.trim(),
        documents: [],
        createdAt: new Date(),
        owner: currentUser.uid,
      });

      const userDocRef = doc(db, "userCollections", currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const { folders: existingFolders = [] } = userDocSnap.data();
        await updateDoc(userDocRef, {
          folders: [...existingFolders, newFolderRef.id],
        });
      }

      await fetchFolders(currentUser.uid, currentUser);
      toast.success("Folder created successfully");
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error("Failed to create folder");
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!confirm("Are you sure you want to delete this folder?")) return;
    try {
      await deleteDoc(doc(db, "folders", folderId));
      // Remove from user's folder list
      if (user) {
        const userDocRef = doc(db, "userCollections", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const { folders: existingFolders = [] } = userDocSnap.data();
          await updateDoc(userDocRef, {
            folders: existingFolders.filter((id: string) => id !== folderId),
          });
        }
        await fetchFolders(user.uid, user);
      }
      toast.success("Folder deleted!");
    } catch (error) {
      toast.error("Failed to delete folder");
    }
  };

  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      router.push("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleRenameFolder = async (folderId: string, newName: string) => {
    if (!newName.trim() || newName === folders.find((f) => f.uid === folderId)?.name) {
      setEditingFolderId(null);
      return;
    }
    try {
      await updateDoc(doc(db, "folders", folderId), {
        name: newName.trim(),
        updatedAt: new Date(),
      });
      setEditingFolderId(null);
      setEditingFolderName("");
      if (user) await fetchFolders(user.uid, user);
      toast.success("Folder renamed!");
    } catch (error) {
      toast.error("Failed to rename folder");
    }
  };

  return (
    <div
      className={`border-r bg-gray-50 flex flex-col transition-all duration-300 ease-in-out ${
        collapsed ? "w-16" : "w-56"
      }`}
    >
      <div className={`flex items-center justify-between p-4 border-b ${collapsed ? "justify-center" : ""}`}>
        {collapsed ? (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCollapsed(false)}>
            <ChevronRight className="h-5 w-5" />
          </Button>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <img src="/images/logo.svg" alt="Bilby Logo" className="w-8 h-8" />
              <span className="font-semibold text-xl">Bilby</span>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCollapsed(true)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {!collapsed && (
        <div className="p-4">
          <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 text-gray-700 hover:bg-gray-200"
                onClick={() => setIsFolderDialogOpen(true)}
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                <span>Add Folder +</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Name your folder</DialogTitle>
              </DialogHeader>
              <Input
                placeholder="e.g. Math Notes"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                className="mt-4"
              />
              <Button
                className="mt-4 w-full"
                onClick={async () => {
                  await handleCreateFolder(folderName);
                  setIsFolderDialogOpen(false);
                  setFolderName("");
                }}
              >
                Create
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <nav className={`flex-1 overflow-auto p-2 w-full ${collapsed ? "hidden" : ""}`}>
        <div className="space-y-1 w-full">
          {folders.map((folder) => (
            <button
              type="button"
              key={folder.uid}
              className={`flex items-center w-full px-3 py-2 text-sm rounded-md hover:bg-gray-200 text-left ${
                activeFolderId === folder.uid ? "bg-gray-200 font-medium" : "text-gray-700"
              }`}
              onClick={() => {
                const pathParts = pathname.split("/").filter(Boolean);
                const username = pathParts[0];
                if (pathParts.length > 1) {
                  router.push(`/${username}`);
                }
                setActiveFolderId(folder.uid);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  const pathParts = pathname.split("/").filter(Boolean);
                  const username = pathParts[0];
                  if (pathParts.length > 1) {
                    router.push(`/${username}`);
                  }
                  setActiveFolderId(folder.uid);
                }
              }}
            >
              <span className="flex-shrink-0">
                <Folder className="h-4 w-4" />
              </span>
              <span className="ml-3 flex-1">
                {editingFolderId === folder.uid ? (
                  <Input
                    value={editingFolderName}
                    autoFocus
                    onChange={(e) => setEditingFolderName(e.target.value)}
                    onBlur={async () => {
                      await handleRenameFolder(folder.uid, editingFolderName);
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter") {
                        await handleRenameFolder(folder.uid, editingFolderName);
                      } else if (e.key === "Escape") {
                        setEditingFolderId(null);
                      }
                    }}
                    className="h-7"
                  />
                ) : (
                  folder.name
                )}
              </span>
            </button>
          ))}
        </div>
      </nav>

      {/* Context menu for folders */}
      {contextMenu.open && (
        <DropdownMenu open={contextMenu.open} onOpenChange={(open) => setContextMenu((c) => ({ ...c, open }))}>
          <DropdownMenuContent
            align="start"
            side="right"
            style={{
              position: "fixed",
              left: contextMenu.x,
              top: contextMenu.y,
              zIndex: 9999,
            }}
            onCloseAutoFocus={() => setContextMenu((c) => ({ ...c, open: false }))}
          >
            <DropdownMenuItem
              onClick={() => {
                if (contextMenu.folderId) {
                  setEditingFolderId(contextMenu.folderId);
                  setEditingFolderName(contextMenu.folderName);
                  setContextMenu((c) => ({ ...c, open: false }));
                }
              }}
            >
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => {
                if (contextMenu.folderId) {
                  handleDeleteFolder(contextMenu.folderId);
                  setContextMenu((c) => ({ ...c, open: false }));
                }
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <div className={`p-4 border-t ${collapsed ? "mt-auto flex justify-center" : ""}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {collapsed ? (
              <Button variant="ghost" size="icon" className="mx-auto h-8 w-8">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.photoURL || "/placeholder.svg?height=32&width=32"} />
                  <AvatarFallback>{username ? username[0] : "U"}</AvatarFallback>
                </Avatar>
              </Button>
            ) : (
              <Button variant="ghost" className="w-full justify-start p-2 hover:bg-gray-200">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.photoURL || "/placeholder.svg?height=32&width=32"} />
                    <AvatarFallback>{username ? username[0] : "U"}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium truncate">{username || "john doe"}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email
                        ? user.email.length > 17
                          ? `${user.email.slice(0, 17)}...`
                          : user.email
                        : "johndoe@example.com"}
                    </p>
                  </div>
                </div>
              </Button>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/profile">Manage Profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem>Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
